import { useEffect } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from './auth-store';

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';
const ROOM = 'admin-nav-counts';

/**
 * Live sidebar badges: subscribe to the `/admin` namespace and refetch the
 * counts whenever the server says a queue moved.
 *
 * The server sends only `{ area }` — never a number. That is what lets this be
 * a plain cache invalidation: the refetch is permission-filtered per viewer, so
 * nothing leaks to an admin who cannot read that queue, and a missed emit on
 * the server degrades to "briefly stale" rather than "wrong".
 *
 * The socket is a FAST PATH, never the source of truth. The query keeps its own
 * focus refetch and slow poll, which is also the only thing covering the worker
 * — it shares no IO instance with the API and cannot emit for the sweeps it
 * owns (auto-complete, autoship, payout batch processing).
 *
 * Keyed on the access token so a refresh reconnects with a valid handshake: the
 * namespace verifies the JWT once, at connect, and would otherwise keep a
 * connection authorised by a token that has since expired.
 */
export function useNavCountsSocket(enabled: boolean) {
  const qc = useQueryClient();
  const token = useAuthStore((s) => s.token);

  useEffect(() => {
    if (!enabled || !token) return;

    let socket: Socket | null = null;
    try {
      socket = io(`${BASE_URL}/admin`, {
        auth: { token },
        transports: ['websocket'],
        // The poll is the real safety net, so a socket that cannot establish
        // should give up quietly instead of retrying forever behind a proxy
        // that does not speak websockets.
        reconnectionAttempts: 5,
      });
    } catch {
      return;
    }

    const join = () => socket?.emit('join', ROOM);
    // Also on every reconnect: room membership does not survive one.
    socket.on('connect', join);
    socket.on('nav-counts:stale', () => {
      void qc.invalidateQueries({ queryKey: ['nav-counts'] });
    });

    return () => {
      socket?.off('connect', join);
      socket?.disconnect();
    };
  }, [enabled, token, qc]);
}
